import React, { useState } from "react";
import { FileText, Download, Pencil, Trash2, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import api, { API } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import { docTypeLabel, docStatusBadge, formatSize } from "@/lib/docStatus";
import { useLang, translateFr as tfr } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const COLUMNS = [
  { key: "label", label: "Document", label_en: "Document", sortable: true },
  { key: "player", label: "Joueur", label_en: "Player", sortable: true },
  { key: "category", label: tfr("Catégorie"), label_en: "Category", sortable: true, className: "hidden md:table-cell" },
  { key: "expiration", label: "Expiration", label_en: "Expiration", sortable: true, className: "hidden sm:table-cell" },
  { key: "status", label: tfr("Statut"), label_en: "Status" },
  { key: "actions", label: "", label_en: "", className: "text-right" },
];
const _colLabel = (col) => { try { return localStorage.getItem("athlynx_lang") === "en" ? col.label_en : col.label; } catch { return col.label; } };

function SortHeader({ col, sort, order, onSort }) {
  if (!col.sortable) return <span>{_colLabel(col)}</span>;
  const active = sort === col.key;
  const Icon = !active ? ChevronsUpDown : order === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={() => onSort(col.key)}
      className={cn(
        "inline-flex items-center gap-1 hover:text-foreground transition-colors",
        active && "text-foreground"
      )}
      data-testid={`documents-sort-${col.key}`}
    >
      {_colLabel(col)}
      <Icon className={cn("h-3 w-3", active ? "opacity-90" : "opacity-40")} />
    </button>
  );
}

function ExpiryPopover({ doc, onChanged }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState((doc.expires_at || "").slice(0, 10));

  const save = async () => {
    try {
      await api.patch(`/players/${doc.player_id}/documents/${doc.id}`, { expires_at: value || null });
      toast.success(tfr("Date d'expiration mise à jour"));
      setOpen(false);
      onChanged();
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(tfr("Erreur lors de la mise à jour"));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
          title={tfr("Modifier l'expiration")}
          aria-label={tfr("Modifier la date d'expiration")}
          data-testid={`document-edit-expiry-${doc.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-3 space-y-2">
        <div className="text-xs font-medium">Date d'expiration</div>
        <Input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-9 text-sm"
          data-testid={`document-expiry-input-${doc.id}`}
        />
        <Button size="sm" className="h-8 w-full text-xs" onClick={save} data-testid={`document-expiry-save-${doc.id}`}>
          {tfr("Enregistrer")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function DeleteDialog({ doc, onChanged }) {
  const { tf } = useLang();
  const remove = async () => {
    try {
      await api.delete(`/players/${doc.player_id}/documents/${doc.id}`);
      toast.success(tf("Document supprimé"));
      onChanged();
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(tf("Erreur lors de la suppression"));
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-danger hover:bg-danger/10 transition-colors"
          title={tf("Supprimer")}
          aria-label={tf("Supprimer le document")}
          data-testid={`document-delete-${doc.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="document-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{tf("Supprimer ce document ?")}</AlertDialogTitle>
          <AlertDialogDescription>
            « {doc.label || doc.filename} » {tf("sera définitivement supprimé. Cette action est irréversible.")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="document-delete-cancel">{tf("Annuler")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={remove}
            className="bg-danger text-white hover:bg-danger/90"
            data-testid="document-delete-confirm"
          >
            {tf("Supprimer")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PlayerCell({ player, onClick }) {
  if (!player) return <span className="text-sm text-muted-foreground">—</span>;
  const initials = (player.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2.5 min-w-0 text-left group/player"
      data-testid={`document-player-link-${player.id}`}
    >
      {player.photo_url ? (
        <img src={player.photo_url} alt="" className="h-8 w-8 rounded-full object-cover border border-border shrink-0" />
      ) : (
        <div className="h-8 w-8 rounded-full bg-secondary border border-border flex items-center justify-center text-[10px] font-semibold text-muted-foreground shrink-0">
          {initials}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-sm font-medium truncate group-hover/player:underline underline-offset-2">{player.name}</div>
        {player.club && <div className="text-[11px] text-muted-foreground truncate">{player.club}</div>}
      </div>
    </button>
  );
}

export default function DocumentsTable({ items, sort, order, onSort, onPlayerClick, onChanged }) {
  const download = (d) => {
    window.open(`${API}/players/${d.player_id}/documents/${d.id}/download`, "_blank", "noopener");
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="documents-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/40">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "px-4 py-3 text-left text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground whitespace-nowrap",
                    col.className
                  )}
                >
                  <SortHeader col={col} sort={sort} order={order} onSort={onSort} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((d) => {
              const badge = docStatusBadge(d.expiry_status, d.days_left);
              return (
                <tr key={d.id} className="hover:bg-secondary/40 transition-colors" data-testid={`document-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0 max-w-[320px]">
                      <div className="shrink-0 h-9 w-9 rounded-lg bg-secondary/80 border border-border flex items-center justify-center">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{d.label || d.filename}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {d.filename}{d.size ? ` · ${formatSize(d.size)}` : ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <PlayerCell player={d.player} onClick={() => onPlayerClick(d.player?.id)} />
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <Badge variant="outline" className="text-[10px] border-border text-muted-foreground font-medium">
                      {docTypeLabel(d.doc_type)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {d.expires_at ? formatDate(d.expires_at) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tabular-nums whitespace-nowrap", badge.cls)}
                      data-testid={`document-status-${d.id}`}
                    >
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-0.5">
                      <ExpiryPopover doc={d} onChanged={onChanged} />
                      <button
                        onClick={() => download(d)}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                        title={tfr("Télécharger")}
                        aria-label={tfr("Télécharger le document")}
                        data-testid={`document-download-${d.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <DeleteDialog doc={d} onChanged={onChanged} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
